import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { marketingStudioAPI } from '../lib/api';
import {
  isMarketingStudioEnabled,
  MARKETING_STUDIO_DISABLED_TEST_ID,
} from '../lib/marketingStudioAvailability';

// Client-side mirror of the backend lifecycle map (marketing_studio_policy). Used
// only to show valid transition buttons; the backend remains the source of truth.
const LIFECYCLE = {
  draft: ['preview', 'in_review', 'archived'],
  preview: ['draft', 'in_review', 'archived'],
  in_review: ['changes_requested', 'approved', 'draft', 'archived'],
  changes_requested: ['draft', 'in_review', 'archived'],
  approved: ['scheduled', 'published', 'changes_requested', 'archived'],
  scheduled: ['published', 'approved', 'archived'],
  published: ['archived', 'scheduled'],
  archived: ['draft'],
};

const CHANNELS = ['website', 'facebook', 'instagram', 'email', 'print', 'video', 'other'];
const ASSET_TYPES = ['image', 'video', 'copy', 'landing_page', 'banner', 'social_post', 'email', 'other'];
const PROVIDERS = ['fable', 'canva', 'figma', 'adobe', 'upload', 'ai_future'];
const DEVICE_SIZES = { desktop: 'w-full', mobile: 'max-w-[375px]', tablet: 'max-w-[768px]' };

function errText(err, fallback) {
  return err?.response?.data?.detail || fallback;
}

export default function MarketingStudioPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { t } = useLanguage();
  const tx = useCallback((key, fallback) => t(`marketingStudio.${key}`, fallback), [t]);

  const enabled = isMarketingStudioEnabled();

  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [asset, setAsset] = useState(null);
  const [versions, setVersions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [device, setDevice] = useState('desktop');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Backend returns 503 when MARKETING_STUDIO_ENABLED is off server-side even
  // though the frontend build flag is on (dual-flag drift). Treat it as disabled.
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  const reportError = useCallback((err, fallbackMsg) => {
    if (err?.response?.status === 503) {
      setBackendUnavailable(true);
      return;
    }
    setError(errText(err, fallbackMsg));
  }, []);

  const [wsForm, setWsForm] = useState({ application: '', name: '', default_country: '', default_language: '' });
  const [assetForm, setAssetForm] = useState({ campaign_name: '', channel: 'website', asset_type: 'image', page: '', section: '', prompt: '', brand_rules: '', target_audience: '' });
  const [refForm, setRefForm] = useState({ provider: 'canva', url: '', asset_type: 'image', notes: '' });
  const [reviewerInput, setReviewerInput] = useState('');
  const [decisionForm, setDecisionForm] = useState({ decision: 'approve', comment: '' });

  const statusLabel = useCallback((s) => tx(`status_${s}`, s), [tx]);

  const loadWorkspaces = useCallback(async () => {
    setError('');
    try {
      const res = await marketingStudioAPI.listWorkspaces();
      setWorkspaces(res.data || []);
    } catch (err) {
      reportError(err, tx('error_generic', 'Something went wrong. Please try again.'));
    }
  }, [tx, reportError]);

  const loadAssets = useCallback(async (workspaceId) => {
    if (!workspaceId) return;
    try {
      const res = await marketingStudioAPI.listAssets({ workspace_id: workspaceId });
      setAssets(res.data || []);
    } catch (err) {
      reportError(err, tx('error_generic', 'Something went wrong. Please try again.'));
    }
  }, [tx, reportError]);

  const loadAssetDetail = useCallback(async (assetId) => {
    if (!assetId) return;
    try {
      const [a, v, p] = await Promise.all([
        marketingStudioAPI.getAsset(assetId),
        marketingStudioAPI.listVersions(assetId),
        marketingStudioAPI.preview(assetId),
      ]);
      setAsset(a.data);
      setVersions(v.data || []);
      setPreview(p.data);
      setReviewerInput((a.data?.assigned_reviewer_ids || []).join(', '));
    } catch (err) {
      reportError(err, tx('error_generic', 'Something went wrong. Please try again.'));
    }
  }, [tx, reportError]);

  useEffect(() => {
    if (enabled && isAdmin) loadWorkspaces();
  }, [enabled, isAdmin, loadWorkspaces]);

  useEffect(() => {
    setSelectedAssetId(null);
    setAsset(null);
    loadAssets(selectedWorkspaceId);
  }, [selectedWorkspaceId, loadAssets]);

  useEffect(() => {
    if (selectedAssetId) loadAssetDetail(selectedAssetId);
  }, [selectedAssetId, loadAssetDetail]);

  const run = useCallback(async (fn, fallbackMsg) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      reportError(err, fallbackMsg || tx('error_generic', 'Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  }, [tx, reportError]);

  const createWorkspace = () => run(async () => {
    const res = await marketingStudioAPI.createWorkspace({
      application: wsForm.application.trim(),
      name: wsForm.name.trim(),
      default_country: wsForm.default_country.trim() || null,
      default_language: wsForm.default_language.trim() || null,
    });
    setWsForm({ application: '', name: '', default_country: '', default_language: '' });
    await loadWorkspaces();
    setSelectedWorkspaceId(res.data.id);
  });

  const createAsset = () => run(async () => {
    const res = await marketingStudioAPI.createAsset({ workspace_id: selectedWorkspaceId, ...assetForm });
    setAssetForm({ campaign_name: '', channel: 'website', asset_type: 'image', page: '', section: '', prompt: '', brand_rules: '', target_audience: '' });
    await loadAssets(selectedWorkspaceId);
    setSelectedAssetId(res.data.id);
  });

  const doTransition = (target) => run(async () => {
    await marketingStudioAPI.transition(selectedAssetId, target);
    await loadAssetDetail(selectedAssetId);
    await loadAssets(selectedWorkspaceId);
  });

  const saveVersion = () => run(async () => {
    await marketingStudioAPI.createVersion(selectedAssetId, { change_note: '' });
    await loadAssetDetail(selectedAssetId);
  });

  const rollback = (versionNumber) => run(async () => {
    await marketingStudioAPI.rollback(selectedAssetId, versionNumber);
    await loadAssetDetail(selectedAssetId);
  });

  const addReference = () => run(async () => {
    await marketingStudioAPI.addReference(selectedAssetId, { ...refForm });
    setRefForm({ provider: 'canva', url: '', asset_type: 'image', notes: '' });
    await loadAssetDetail(selectedAssetId);
  });

  const removeReference = (refId) => run(async () => {
    await marketingStudioAPI.deleteReference(selectedAssetId, refId);
    await loadAssetDetail(selectedAssetId);
  });

  const assignReviewers = () => run(async () => {
    const ids = reviewerInput.split(',').map((s) => s.trim()).filter(Boolean);
    await marketingStudioAPI.assignReviewers(selectedAssetId, ids);
    await loadAssetDetail(selectedAssetId);
  });

  const submitDecision = () => run(async () => {
    await marketingStudioAPI.decision(selectedAssetId, { ...decisionForm });
    setDecisionForm({ decision: 'approve', comment: '' });
    await loadAssetDetail(selectedAssetId);
    await loadAssets(selectedWorkspaceId);
  });

  const previewDevices = useMemo(
    () => (preview?.device_intents?.length ? preview.device_intents : ['desktop', 'mobile', 'tablet']),
    [preview],
  );

  // ── guards ────────────────────────────────────────────────────────────────
  if (!enabled) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid={MARKETING_STUDIO_DISABLED_TEST_ID}>
        <h1 className="text-2xl font-semibold text-slate-800">{tx('title', 'Marketing Studio')}</h1>
        <p className="mt-3 text-slate-600">{tx('disabled_notice', 'Marketing Studio is not enabled in this environment.')}</p>
      </div>
    );
  }
  if (!user || !isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold text-slate-800">{tx('title', 'Marketing Studio')}</h1>
        <p className="mt-3 text-slate-600">{tx('admin_only', 'Marketing Studio is available to admins only.')}</p>
      </div>
    );
  }
  if (backendUnavailable) {
    // Backend flag is off (503): show the same disabled notice as the build-flag-off state.
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid={MARKETING_STUDIO_DISABLED_TEST_ID}>
        <h1 className="text-2xl font-semibold text-slate-800">{tx('title', 'Marketing Studio')}</h1>
        <p className="mt-3 text-slate-600">{tx('disabled_notice', 'Marketing Studio is not enabled in this environment.')}</p>
      </div>
    );
  }

  const inputCls = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm';
  const cardCls = 'bg-white rounded-xl border border-slate-200 shadow-sm p-4';
  const btnCls = 'rounded-lg bg-slate-800 text-white text-sm px-3 py-2 disabled:opacity-50';
  const chipCls = 'rounded-lg border border-slate-300 text-sm px-3 py-1 disabled:opacity-50';

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{tx('title', 'Marketing Studio')}</h1>
          <p className="text-slate-500 text-sm">{tx('subtitle', 'Campaign assets, prompts, preview and sign-off')}</p>
        </div>
        <button type="button" className={chipCls} onClick={() => navigate('/admin')}>
          {tx('back_to_admin', 'Back to admin')}
        </button>
      </div>

      {error ? <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div> : null}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workspaces */}
        <div className={cardCls}>
          <h2 className="font-semibold text-slate-700 mb-3">{tx('workspaces', 'Workspaces')}</h2>
          {workspaces.length === 0 ? <p className="text-sm text-slate-400 mb-3">{tx('no_workspaces', 'No workspaces yet.')}</p> : null}
          <ul className="space-y-1 mb-4">
            {workspaces.map((ws) => (
              <li key={ws.id}>
                <button
                  type="button"
                  onClick={() => setSelectedWorkspaceId(ws.id)}
                  className={`w-full text-left rounded-lg px-3 py-2 text-sm ${selectedWorkspaceId === ws.id ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'}`}
                >
                  {ws.name} <span className="text-slate-400">· {ws.application}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-slate-100 pt-3 space-y-2">
            <div className="text-xs font-medium text-slate-500">{tx('new_workspace', 'New workspace')}</div>
            <input className={inputCls} placeholder={tx('application', 'Application')} value={wsForm.application} onChange={(e) => setWsForm({ ...wsForm, application: e.target.value })} />
            <input className={inputCls} placeholder={tx('name', 'Name')} value={wsForm.name} onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })} />
            <div className="flex gap-2">
              <input className={inputCls} placeholder={tx('default_country', 'Default country')} value={wsForm.default_country} onChange={(e) => setWsForm({ ...wsForm, default_country: e.target.value })} />
              <input className={inputCls} placeholder={tx('default_language', 'Default language')} value={wsForm.default_language} onChange={(e) => setWsForm({ ...wsForm, default_language: e.target.value })} />
            </div>
            <button type="button" className={btnCls} disabled={busy || !wsForm.application.trim() || !wsForm.name.trim()} onClick={createWorkspace}>
              {tx('create', 'Create')}
            </button>
          </div>
        </div>

        {/* Assets */}
        <div className={cardCls}>
          <h2 className="font-semibold text-slate-700 mb-3">{tx('assets', 'Assets')}</h2>
          {!selectedWorkspaceId ? (
            <p className="text-sm text-slate-400">{tx('select_workspace_hint', 'Select a workspace to see its assets.')}</p>
          ) : (
            <>
              {assets.length === 0 ? <p className="text-sm text-slate-400 mb-3">{tx('no_assets', 'No assets in this workspace yet.')}</p> : null}
              <ul className="space-y-1 mb-4">
                {assets.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedAssetId(a.id)}
                      className={`w-full text-left rounded-lg px-3 py-2 text-sm ${selectedAssetId === a.id ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'}`}
                    >
                      {a.campaign_name}
                      <span className="ml-2 inline-block rounded bg-slate-100 text-slate-600 text-xs px-2 py-0.5">{statusLabel(a.status)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="border-t border-slate-100 pt-3 space-y-2">
                <div className="text-xs font-medium text-slate-500">{tx('new_asset', 'New asset')}</div>
                <input className={inputCls} placeholder={tx('campaign_name', 'Campaign name')} value={assetForm.campaign_name} onChange={(e) => setAssetForm({ ...assetForm, campaign_name: e.target.value })} />
                <div className="flex gap-2">
                  <select className={inputCls} value={assetForm.channel} onChange={(e) => setAssetForm({ ...assetForm, channel: e.target.value })} aria-label={tx('channel', 'Channel')}>
                    {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select className={inputCls} value={assetForm.asset_type} onChange={(e) => setAssetForm({ ...assetForm, asset_type: e.target.value })} aria-label={tx('asset_type', 'Asset type')}>
                    {ASSET_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <input className={inputCls} placeholder={tx('page', 'Page')} value={assetForm.page} onChange={(e) => setAssetForm({ ...assetForm, page: e.target.value })} />
                  <input className={inputCls} placeholder={tx('section', 'Section')} value={assetForm.section} onChange={(e) => setAssetForm({ ...assetForm, section: e.target.value })} />
                </div>
                <input className={inputCls} placeholder={tx('target_audience', 'Target audience')} value={assetForm.target_audience} onChange={(e) => setAssetForm({ ...assetForm, target_audience: e.target.value })} />
                <textarea className={inputCls} placeholder={tx('prompt', 'Prompt')} value={assetForm.prompt} onChange={(e) => setAssetForm({ ...assetForm, prompt: e.target.value })} />
                <input className={inputCls} placeholder={tx('brand_rules', 'Brand rules')} value={assetForm.brand_rules} onChange={(e) => setAssetForm({ ...assetForm, brand_rules: e.target.value })} />
                <button type="button" className={btnCls} disabled={busy || !assetForm.campaign_name.trim()} onClick={createAsset}>
                  {tx('create', 'Create')}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Asset detail */}
        <div className={cardCls}>
          {!asset ? (
            <p className="text-sm text-slate-400">{tx('select_asset_hint', 'Select an asset to see details.')}</p>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-slate-700">{asset.campaign_name}</h2>
                  <span className="inline-block rounded bg-slate-100 text-slate-700 text-xs px-2 py-0.5">{statusLabel(asset.status)}</span>
                </div>
                <div className="text-xs text-slate-400">{tx('version', 'Version')} {asset.current_version}</div>
              </div>

              {/* Lifecycle */}
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">{tx('lifecycle', 'Lifecycle')}</div>
                <div className="flex flex-wrap gap-2">
                  {(LIFECYCLE[asset.status] || []).map((target) => (
                    <button key={target} type="button" className={chipCls} disabled={busy} onClick={() => doTransition(target)}>
                      {tx('move_to', 'Move to')} {statusLabel(target)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sign-off */}
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">{tx('sign_off', 'Sign-off')}</div>
                <p className="text-xs text-slate-400 mb-2">{tx('decision_hint', 'Decisions can be recorded while the asset is in review.')}</p>
                <input className={inputCls} placeholder={tx('reviewer_ids_hint', 'Comma-separated admin user IDs')} value={reviewerInput} onChange={(e) => setReviewerInput(e.target.value)} />
                <button type="button" className={`${chipCls} mt-2`} disabled={busy} onClick={assignReviewers}>{tx('assign_reviewers', 'Assign reviewers')}</button>
                <div className="flex gap-2 mt-2">
                  <select className={inputCls} value={decisionForm.decision} onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value })} aria-label={tx('decision', 'Decision')}>
                    <option value="approve">{tx('approve', 'Approve')}</option>
                    <option value="request_changes">{tx('request_changes', 'Request changes')}</option>
                    <option value="reject">{tx('reject', 'Reject')}</option>
                  </select>
                  <button type="button" className={btnCls} disabled={busy || asset.status !== 'in_review'} onClick={submitDecision}>{tx('submit_decision', 'Submit decision')}</button>
                </div>
                <input className={`${inputCls} mt-2`} placeholder={tx('comment', 'Comment')} value={decisionForm.comment} onChange={(e) => setDecisionForm({ ...decisionForm, comment: e.target.value })} />
              </div>

              {/* Provider references */}
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">{tx('references', 'Provider references')}</div>
                {(asset.asset_references || []).length === 0 ? <p className="text-xs text-slate-400">{tx('no_references', 'No provider references yet.')}</p> : null}
                <ul className="space-y-1">
                  {(asset.asset_references || []).map((ref) => (
                    <li key={ref.id} className="flex items-center justify-between text-sm">
                      <a className="text-signal-blue underline truncate" href={ref.url} target="_blank" rel="noreferrer">{ref.provider} · {ref.asset_type}</a>
                      <button type="button" className="text-xs text-red-500" disabled={busy} onClick={() => removeReference(ref.id)}>✕</button>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <select className={inputCls} value={refForm.provider} onChange={(e) => setRefForm({ ...refForm, provider: e.target.value })} aria-label={tx('provider', 'Provider')}>
                      {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <input className={inputCls} placeholder={tx('asset_type', 'Asset type')} value={refForm.asset_type} onChange={(e) => setRefForm({ ...refForm, asset_type: e.target.value })} />
                  </div>
                  <input className={inputCls} placeholder={tx('url', 'URL')} value={refForm.url} onChange={(e) => setRefForm({ ...refForm, url: e.target.value })} />
                  <button type="button" className={chipCls} disabled={busy || !refForm.url.trim()} onClick={addReference}>{tx('add_reference', 'Add reference')}</button>
                </div>
              </div>

              {/* Versions */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-slate-500 mb-1">{tx('versions', 'Version history')}</div>
                  <button type="button" className="text-xs text-slate-600 underline" disabled={busy} onClick={saveVersion}>{tx('save_version', 'Save version')}</button>
                </div>
                {versions.length === 0 ? <p className="text-xs text-slate-400">{tx('no_versions', 'No versions yet.')}</p> : null}
                <ul className="space-y-1">
                  {versions.map((v) => (
                    <li key={`${v.asset_id}-${v.version_number}`} className="flex items-center justify-between text-sm">
                      <span>v{v.version_number} <span className="text-slate-400">{v.change_note}</span></span>
                      <button type="button" className="text-xs text-slate-600 underline" disabled={busy} onClick={() => rollback(v.version_number)}>{tx('rollback', 'Roll back')}</button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Digital Twin preview */}
      {asset && preview ? (
        <div className={`${cardCls} mt-6`}>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-700">{tx('preview', 'Digital Twin preview')}</h2>
            <div className="flex gap-2">
              {previewDevices.map((d) => (
                <button key={d} type="button" className={`${chipCls} ${device === d ? 'bg-slate-100 font-medium' : ''}`} onClick={() => setDevice(d)}>
                  {tx(`device_${d}`, d)}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-1">{tx('preview_only_note', 'Preview only. Publishing does not push to a live site in this phase.')}</p>
          <div className="mt-4 flex justify-center">
            <div className={`border border-slate-200 rounded-xl bg-slate-50 p-4 ${DEVICE_SIZES[device] || 'w-full'}`}>
              <div className="text-sm text-slate-600">
                <div><span className="text-slate-400">{tx('page', 'Page')}:</span> {preview.digital_twin?.page || '—'}</div>
                <div><span className="text-slate-400">{tx('section', 'Section')}:</span> {preview.digital_twin?.section || '—'}</div>
                <div><span className="text-slate-400">{tx('variant', 'Variant')}:</span> {preview.variant?.country || '—'} / {preview.variant?.language || '—'}</div>
                <div><span className="text-slate-400">{tx('status', 'Status')}:</span> {statusLabel(preview.status)}</div>
              </div>
              {(preview.references || []).length > 0 ? (
                <div className="mt-3 border-t border-slate-200 pt-3">
                  {preview.references.map((ref) => (
                    <a key={ref.id} className="block text-signal-blue underline text-sm truncate" href={ref.url} target="_blank" rel="noreferrer">
                      {tx('open_preview', 'Open external asset')} · {ref.provider}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
